import type { Trip } from '../redux/trip/trip.types';
import { Marker, MarkerType } from '../types/MapMarker';

export const DESCRIPTION_MAX_LENGTH = 500;

/**
 * Country for a trip, taken from the last segment of its autocomplete label
 * ("Paris, France" -> "France"). Avoids a geocoding call per trip; returns null
 * for a bare single-segment label we can't tell apart from a country.
 */
export function getTripCountry(place?: unknown): string | null {
  const parts = String(place ?? '')
    .split(',')
    .map((part) => part.trim());
  const country = parts[parts.length - 1];
  return parts.length > 1 && country ? country.toLowerCase() : null;
}

/** Distinct countries across trips. A one-segment label counts as its own country ("Japan"). */
export function countCountries(trips: Trip[]): number {
  const countries = new Set<string>();
  for (const trip of trips) {
    const label = String(trip.place ?? '').trim();
    const country = getTripCountry(label) ?? (label ? label.toLowerCase() : null);
    if (country) {
      countries.add(country);
    }
  }
  return countries.size;
}

/** One marker per trip; trips without usable coordinates are skipped. */
export function getTripMarkers(trips: Trip[]): Marker[] {
  return trips.flatMap((trip) => {
    const lat = trip.location?.latitude;
    const lng = trip.location?.longitude;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return [];
    }
    return [
      {
        id: trip.id,
        markerType: MarkerType.EMOJI,
        location: { lat, lng },
        data: { emoji: '✈️', description: trip.title, isHighlighted: false },
      },
    ];
  });
}
